import React from 'react';
import { DRAWING_MODES, SPACE_TYPES, SENSOR_TYPES, SPACE_TYPE_COLORS, SENSOR_COLORS } from '../../utils/constants';
import { transformPoint, getBoxCoordinates } from '../../utils/geometry';
import { formatMeasurement } from '../../utils/conversion';
import ScaleTool from '../ScaleTool';

/**
 * MeasurementOverlay component for displaying measurements and shapes
 * 
 * This component is designed to be used inside a transformed container,
 * so it works with raw coordinates that don't need additional transformation.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.drawing - Drawing state
 * @param {Array} props.boxes - Array of box objects
 * @param {Array} props.shapes - Array of shape objects
 * @param {Object} props.selectedSpace - Currently selected space
 * @param {number} props.scale - Current zoom scale (not used directly in this component)
 * @param {Object} props.position - Current pan position (not used directly in this component)
 * @param {number} props.pixelsPerMeter - Scale in pixels per meter
 * @param {Object} props.referenceLine - Reference line object
 * @param {Object} props.spaceTypeVisibility - Object tracking visibility of space types
 * @param {boolean} props.shiftKeyPressed - Whether the shift key is pressed
 * @param {Object} props.currentMousePos - Current mouse position for preview
 * @param {boolean} props.colorBySensorType - Whether boxes are being colored by sensor type in parent component
 * @returns {JSX.Element} MeasurementOverlay component
 */
const MeasurementOverlay = ({
  drawing,
  boxes,
  shapes,
  selectedSpace,
  scale,
  position,
  pixelsPerMeter,
  referenceLine,
  spaceTypeVisibility,
  shiftKeyPressed = false,
  currentMousePos = null,
  colorBySensorType = false
}) => {
  const {
    drawingMode,
    boxStart,
    boxEnd,
    shapePoints,
    isClosingShape,
    measurePoints,
    measureDistance
  } = drawing;
  
  /**
   * Creates a darker version of a given color for borders
   * @param {string} color - Hex color code
   * @returns {string} Darker version of the color
   */
  const getDarkerColor = (color) => {
    // If color is not a hex code, return a default dark color
    if (!color || !color.startsWith('#')) return '#333333';
    
    // Convert hex to RGB
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    
    // Make each component darker by multiplying by 0.7
    const darkerR = Math.floor(r * 0.7);
    const darkerG = Math.floor(g * 0.7);
    const darkerB = Math.floor(b * 0.7);
    
    // Convert back to hex
    return `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;
  };
  
  // Helper function to transform a point
  // We don't need to apply any transformation here since the SVG is already inside 
  // the transformed container - we just use the original coordinates
  const transform = (point) => {
    if (!point) return null;
    // Return raw coordinates - no transformation needed
    return point;
  };
  
  // Helper function to get box coordinates
  // Similarly, we don't need to apply transformations here
  const getBoxCoords = (start, end) => getBoxCoordinates(start, end, 1, {x: 0, y: 0});
  
  // Filter boxes based on spaceTypeVisibility
  const visibleBoxes = boxes.filter(box => {
    // Check if box should be visible based on space category
    if (!box.visible) return false;
    if (!spaceTypeVisibility) return true;
    
    const category = box.spaceCategory || SPACE_TYPES.DESK;
    return spaceTypeVisibility[category];
  });
  
  // Filter shapes based on spaceTypeVisibility
  const visibleShapes = shapes.filter(shape => {
    // Check if shape should be visible based on space category
    if (!shape.visible) return false;
    if (!spaceTypeVisibility) return true;
    
    const category = shape.spaceCategory || SPACE_TYPES.DESK;
    return spaceTypeVisibility[category];
  });
  
  // Add ScaleTool for measurement preview and reference line
  const renderScaleTool = () => {
    const isSettingScale = drawingMode === DRAWING_MODES.SETTING_SCALE;
    
    return (
      <ScaleTool
        renderType="svg"
        isSettingScale={isSettingScale}
        measurePoints={measurePoints}
        measureDistance={measureDistance}
        referenceLine={referenceLine}
        pixelsPerMeter={pixelsPerMeter}
        zoom={scale}
        shiftPressed={shiftKeyPressed}
        currentMousePos={currentMousePos}
      />
    );
  };
  
  return (
    <g className="measurement-overlay">
      {/* Render reference line if exists */}
      {referenceLine && (
        <g className="reference-line">
          <line 
            x1={transform(referenceLine.points[0]).x} 
            y1={transform(referenceLine.points[0]).y} 
            x2={transform(referenceLine.points[1]).x} 
            y2={transform(referenceLine.points[1]).y} 
            stroke="rgba(255, 0, 0, 0.5)" 
            strokeWidth="2" 
            strokeDasharray="5,5" 
          />
          <circle 
            cx={transform(referenceLine.points[0]).x} 
            cy={transform(referenceLine.points[0]).y} 
            r="5" 
            fill="rgba(255, 0, 0, 0.5)" 
          />
          <circle 
            cx={transform(referenceLine.points[1]).x} 
            cy={transform(referenceLine.points[1]).y} 
            r="5" 
            fill="rgba(255, 0, 0, 0.5)" 
          />
        </g>
      )}
      
      {/* Render measurement points and their preview */}
      {renderScaleTool()}
      
      {/* Draw current box preview */}
      {boxStart && boxEnd && (
        <rect
          x={getBoxCoords(boxStart, boxEnd)?.x}
          y={getBoxCoords(boxStart, boxEnd)?.y}
          width={getBoxCoords(boxStart, boxEnd)?.width}
          height={getBoxCoords(boxStart, boxEnd)?.height}
          fill="rgba(0, 100, 255, 0.2)"
          stroke="blue"
          strokeWidth="2"
        />
      )}
      
      {/* Draw free-form shape in progress */}
      {drawingMode === DRAWING_MODES.DRAWING_SHAPE && shapePoints.length > 0 && (
        <g>
          {/* Draw points */}
          {shapePoints.map((point, index) => (
            <circle
              key={index}
              cx={transform(point).x}
              cy={transform(point).y}
              r={index === 0 && isClosingShape ? "8" : "5"}
              fill={index === 0 ? (isClosingShape ? "green" : "red") : "red"}
              stroke={index === 0 ? "white" : "none"}
              strokeWidth="2"
            />
          ))}
          
          {/* Draw lines connecting points */}
          {shapePoints.map((point, index) => {
            if (index === 0) return null;
            const prevPoint = shapePoints[index - 1];
            return (
              <line
                key={`line-${index}`}
                x1={transform(prevPoint).x}
                y1={transform(prevPoint).y}
                x2={transform(point).x}
                y2={transform(point).y}
                stroke="red"
                strokeWidth="2"
              />
            );
          })}
        </g>
      )}
    </g>
  );
};

export default MeasurementOverlay; 